import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronRight, Check, User, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import { CATEGORIES } from '../data/demo';
import { useI18n } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCOUNT_TYPES = [
  { id: 'professional', title: 'Profesional / Trabajador', desc: 'Técnico/a autónomo o en plantilla. Recibes ofertas y puedes publicar como profesional.', Icon: User },
  { id: 'company', title: 'Empresa', desc: 'Productora, teatro, servicios técnicos. Publicas ofertas a nombre de tu empresa.', Icon: Building2 },
];

const AVAILABILITY = [
  { id: 'available', title: 'Disponible ahora' },
  { id: 'onTour', title: 'De bolos / gira' },
];

const EXPERIENCE = ['Menos de 1 año', '1-3 años', '3-7 años', '7-15 años', 'Más de 15 años'];

const inp = 'w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm';
const lbl = 'block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2';

const Inner = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    accountType: '', category: '', categories: [], location: '', experience: '',
    availability: 'available', skills: '', bio: '',
    company_name: '', company_tax_id: '', company_email: '', company_phone: '',
    company_website: '', company_address: '', company_sector: '', company_description: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/profile/me`, { credentials: 'include' });
        if (!res.ok) return;
        const p = await res.json();
        setForm((f) => ({
          ...f,
          accountType: p.account_type || '',
          category: p.category || '',
          categories: p.categories?.length ? p.categories : (p.category ? [p.category] : []),
          location: p.location || '',
          experience: p.experience || '',
          availability: p.availability || 'available',
          skills: (p.skills || []).join(', '),
          bio: p.bio || '',
          company_name: p.company_name || '',
          company_tax_id: p.company_tax_id || '',
          company_email: p.company_email || '',
          company_phone: p.company_phone || '',
          company_website: p.company_website || '',
          company_address: p.company_address || '',
          company_sector: p.company_sector || '',
          company_description: p.company_description || '',
        }));
      } catch { /* noop */ }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const base = { account_type: form.accountType };
      const payload = form.accountType === 'company'
        ? {
          ...base,
          company_name: form.company_name || null,
          company_tax_id: form.company_tax_id || null,
          company_email: form.company_email || null,
          company_phone: form.company_phone || null,
          company_website: form.company_website || null,
          company_address: form.company_address || null,
          company_sector: form.company_sector || null,
          company_description: form.company_description || null,
        }
        : {
          ...base,
          role: 'Profesional técnico',
          category: form.categories[0] || form.category || null,
          categories: form.categories,
          location: form.location || null,
          experience: form.experience || null,
          availability: form.availability || 'available',
          bio: form.bio || null,
          skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
          visibility: {
            phone: false, email: false, role: true, category: true,
            location: true, experience: true, availability: true,
            bio: true, skills: true, certifications: false,
          },
        };
      const res = await fetch(`${API}/profile/me`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success('Perfil completado');
      setTimeout(() => navigate('/mi-perfil'), 700);
    } catch {
      toast.error('No se pudo guardar. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const accountStep = {
    title: '¿Cómo quieres usar Stage Crew?',
    subtitle: 'Elige tu tipo de cuenta. Podrás cambiarlo más adelante en Ajustes.',
    testid: 'onb-step-account',
    content: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ACCOUNT_TYPES.map((a) => (
          <button key={a.id} data-testid={`onb-acct-${a.id}`} type="button" onClick={() => set('accountType', a.id)}
            className={`text-left sc-card p-5 transition-all ${form.accountType === a.id ? 'border-amber-500/60 shadow-[0_0_0_1px_rgba(245,158,11,0.4)]' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <a.Icon size={22} className="text-amber-500" />
              {form.accountType === a.id && <Check size={14} className="text-amber-500" />}
            </div>
            <span className="block text-base font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{a.title}</span>
            <span className="text-xs text-slate-400 leading-relaxed">{a.desc}</span>
          </button>
        ))}
      </div>
    ),
    canNext: !!form.accountType,
  };

  const companyStep = {
    title: 'Datos de tu empresa',
    subtitle: 'Rellena los datos reales de tu empresa. Al completar los obligatorios (*) aparecerás con el sello «Verificada» para que la gente confíe y pueda contactarte.',
    testid: 'onb-step-company',
    content: (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={lbl}>Nombre de la empresa *</label><input data-testid="onb-company-name" className={inp} value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="LumenTech Escénica S.L." /></div>
          <div><label className={lbl}>CIF / NIF *</label><input data-testid="onb-company-tax" className={inp} value={form.company_tax_id} onChange={(e) => set('company_tax_id', e.target.value)} placeholder="B12345678" /></div>
          <div><label className={lbl}>Email de contacto *</label><input data-testid="onb-company-email" type="email" className={inp} value={form.company_email} onChange={(e) => set('company_email', e.target.value)} placeholder="contacto@empresa.com" /></div>
          <div><label className={lbl}>Teléfono *</label><input data-testid="onb-company-phone" className={inp} value={form.company_phone} onChange={(e) => set('company_phone', e.target.value)} placeholder="+34 934 000 000" /></div>
          <div><label className={lbl}>Dirección *</label><input data-testid="onb-company-address" className={inp} value={form.company_address} onChange={(e) => set('company_address', e.target.value)} placeholder="C/ Ejemplo 1, Barcelona" /></div>
          <div><label className={lbl}>Web</label><input data-testid="onb-company-website" className={inp} value={form.company_website} onChange={(e) => set('company_website', e.target.value)} placeholder="https://empresa.com" /></div>
          <div><label className={lbl}>Sector / especialidad</label><input data-testid="onb-company-sector" className={inp} value={form.company_sector} onChange={(e) => set('company_sector', e.target.value)} placeholder="Iluminación y vídeo" /></div>
        </div>
        <div><label className={lbl}>Descripción</label><textarea data-testid="onb-company-description" rows={4} className={inp + ' resize-none'} value={form.company_description} onChange={(e) => set('company_description', e.target.value)} placeholder="Cuéntanos brevemente a qué se dedica tu empresa…" /></div>
      </div>
    ),
    canNext: true,
  };

  const proSteps = [
    {
      title: 'Especialidades', subtitle: 'Marca todas las áreas en las que trabajas. La primera que elijas será tu especialidad principal.', testid: 'onb-step-category',
      content: (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.id} data-testid={`onb-cat-${c.id}`} type="button" aria-pressed={form.categories.includes(c.id)}
              onClick={() => set('categories', form.categories.includes(c.id) ? form.categories.filter((x) => x !== c.id) : [...form.categories, c.id])}
              className={`sc-card p-4 flex items-center gap-3 text-left transition-colors ${form.categories.includes(c.id) ? 'border-amber-500/70 bg-amber-500/10' : ''}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${form.categories.includes(c.id) ? 'bg-amber-500 border-amber-500 text-slate-950' : 'border-[#2A2F3D]'}`}>{form.categories.includes(c.id) && <span className="text-[10px] font-bold">✓</span>}</span>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.accent }} />
              <span className="text-sm text-slate-200">{c.name}{form.categories[0] === c.id && <span className="text-[10px] text-amber-500 font-mono uppercase ml-1">· principal</span>}</span>
            </button>
          ))}
        </div>
      ),
      canNext: form.categories.length > 0,
    },
    {
      title: 'Ubicación y experiencia', subtitle: 'Ayúdanos a mostrarte ofertas y profesionales cercanos.', testid: 'onb-step-location',
      content: (
        <div className="space-y-4">
          <div><label className={lbl}>Ubicación</label><input data-testid="onb-location" className={inp} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Barcelona, Catalunya" /></div>
          <div>
            <label className={lbl}>Experiencia</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {EXPERIENCE.map((e) => (
                <button key={e} type="button" data-testid={`onb-exp-${e.replace(/\s+/g, '-')}`} onClick={() => set('experience', e)}
                  className={`text-xs sc-card px-3 py-2 ${form.experience === e ? 'border-amber-500/60 text-amber-500' : 'text-slate-300'}`}>{e}</button>
              ))}
            </div>
          </div>
          <div>
            <label className={lbl}>Disponibilidad</label>
            <div className="flex gap-2">
              {AVAILABILITY.map((a) => (
                <button key={a.id} type="button" data-testid={`onb-avail-${a.id}`} onClick={() => set('availability', a.id)}
                  className={`sc-card px-4 py-2 text-sm ${form.availability === a.id ? 'border-amber-500/60 text-amber-500' : 'text-slate-300'}`}>{a.title}</button>
              ))}
            </div>
          </div>
        </div>
      ),
      canNext: !!form.location && !!form.experience,
    },
    {
      title: 'Habilidades y bio', subtitle: 'Añade las principales herramientas / consolas que dominas y una breve presentación.', testid: 'onb-step-skills',
      content: (
        <div className="space-y-4">
          <div><label className={lbl}>Habilidades técnicas (separadas por coma)</label><input data-testid="onb-skills" className={inp} value={form.skills} onChange={(e) => set('skills', e.target.value)} placeholder="GrandMA3, DMX, Wysiwyg" /></div>
          <div><label className={lbl}>Bio corta</label><textarea data-testid="onb-bio" rows={4} className={inp + ' resize-none'} value={form.bio} onChange={(e) => set('bio', e.target.value)} placeholder="Cuéntanos brevemente tu perfil profesional…" /></div>
        </div>
      ),
      canNext: true,
    },
  ];

  const steps = form.accountType === 'company' ? [accountStep, companyStep] : [accountStep, ...proSteps];
  const current = steps[Math.min(step, steps.length - 1)];

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24" data-testid="onboarding-page">
        <div className="sc-mono-eyebrow text-amber-500 mb-4">Paso {step + 1} de {steps.length}</div>
        <div className="w-full h-1 bg-[#2A2F3D] rounded-full overflow-hidden mb-8">
          <div className="h-full bg-amber-500 transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{current.title}</h1>
        <p className="text-slate-400 mb-8">{current.subtitle}</p>

        <div data-testid={current.testid}>{current.content}</div>

        <div className="mt-10 flex items-center justify-between">
          <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            className="text-sm font-mono uppercase tracking-widest text-slate-400 hover:text-amber-500 disabled:opacity-40" data-testid="onb-prev">← Anterior</button>
          {step < steps.length - 1 ? (
            <button type="button" onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))} disabled={!current.canNext} data-testid="onb-next"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">Siguiente <ChevronRight size={16} /></button>
          ) : (
            <button type="button" onClick={submit} disabled={saving} data-testid="onb-submit"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">{saving ? 'Guardando…' : 'Finalizar'}</button>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

const OnboardingPage = () => <AuthGate><Inner /></AuthGate>;
export default OnboardingPage;
